import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '../contexts/SessionContext';
import { useCompany } from '../contexts/CompanyContext';
import { Link } from 'react-router-dom';
import { getSidebarCollapsed, toggleSidebarCollapsed, SIDEBAR_CHANGED_EVENT } from '../utils/sidebarBus';
import CreateOrganizationModal, { OPEN_CREATE_ORG_MODAL_EVENT } from './CreateOrganizationModel';
import NewVersionBanner from './NewVersionBanner';

interface MainLayoutProps {
  children: React.ReactNode | ((props: { toggleSidebar: () => void }) => React.ReactNode);
}

interface Company {
  company_id: string;
  company_name: string;
  address: string;
  country_iso?: string;
}

/**
 * MainLayout - Modern SaaS sidebar with toggle functionality and state persistence
 * Provides the main sidebar and content wrapper
 */
export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  // Sidebar collapse: NO STATE, DOM controlled
  const containerRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const togglePathRef = useRef<SVGPathElement>(null);
  const mountedRef = useRef(true);

  const { user, isAdmin, isUserAdmin, isSuperadmin, orgs, signOut, activeOrg, permissions, profile, refresh } = useSession();
  const [userCompanies, setUserCompanies] = useState<Company[]>([]);
  const { selectedCompany, setSelectedCompany } = useCompany();
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);
  const [userRole, setUserRole] = useState<number>(0);
  const location = useLocation();
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [createOrgDefaultCountry, setCreateOrgDefaultCountry] = useState<string | undefined>(undefined);
  
  // Cleanup on unmount - prevent DOM mutations after unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  useEffect(() => {
    // Use orgs from SessionContext instead of fetching
    if (orgs && orgs.length > 0) {
      const companies = orgs.map(org => ({
        company_id: org.orgId,
        company_name: org.orgName,
        address: org.address || '',
        country_iso: org.countryIso || '',
      }));
      setUserCompanies(companies);
      setLoading(false);
    } else {
      setUserCompanies([]);
      setLoading(false);
    }
  }, [orgs]);

  useEffect(() => {
    const open = (e: Event) => {
      setShowCompanyDropdown(false);
      const detail = (e as CustomEvent)?.detail;
      setCreateOrgDefaultCountry(detail?.country || undefined);
      setShowCreateOrgModal(true);
    };
    window.addEventListener(OPEN_CREATE_ORG_MODAL_EVENT, open);
    return () => window.removeEventListener(OPEN_CREATE_ORG_MODAL_EVENT, open);
  }, []);
  
  const applySidebarCollapsedToDOM = useCallback((isCollapsed: boolean) => {
    // Guard against DOM mutations after unmount
    if (!mountedRef.current) return;
    
    if (sidebarRef.current) {
      sidebarRef.current.classList.toggle('sidebar-collapsed', isCollapsed);
    }
    if (mainRef.current) {
      mainRef.current.classList.toggle('sidebar-collapsed', isCollapsed);
    }
    if (containerRef.current) {
      containerRef.current.setAttribute('data-sidebar-collapsed', isCollapsed ? 'true' : 'false');
    }
    // Update chevron direction without re-render
    if (togglePathRef.current) {
      togglePathRef.current.setAttribute('d', isCollapsed ? 'M9 18l6-6-6-6' : 'M15 18l-6-6 6-6');
    }
  }, []);

  // init + listen (NO RE-RENDER on toggle)
  useEffect(() => {
    applySidebarCollapsedToDOM(getSidebarCollapsed());

    const onChanged = (e: any) => {
      // Guard against events firing after unmount
      if (!mountedRef.current) return;
      const next = !!e?.detail?.collapsed;
      applySidebarCollapsedToDOM(next);
    };
    document.addEventListener(SIDEBAR_CHANGED_EVENT, onChanged);
    return () => document.removeEventListener(SIDEBAR_CHANGED_EVENT, onChanged);
  }, [applySidebarCollapsedToDOM]);

  const toggleSidebar = useCallback(() => {
    toggleSidebarCollapsed();
  }, []);
  
  useEffect(() => {
    if (activeOrg) {
      setUserRole(activeOrg.roleId);
    } else {
      setUserRole(0);
    }
  }, [activeOrg]);

  const navigate = useNavigate();

  const handleSignOut = async () => {
    // Let AuthContext handle all sign-out logic and redirect
    // No need to redirect here - signOut() triggers SIGNED_OUT event
    // which handles state clearing and redirect
    await signOut();
  };

  const handleCompanyChange = (company: Company) => {
    // Guard against company change during sign-out
    if (!user) return;
    
    setSelectedCompany(company);
    setShowCompanyDropdown(false);
    
    const currPath = location.pathname;
    const companyId = company.company_id;

    if (currPath.includes('/dashboard')) {
      navigate(`/dashboard/${companyId}`);
    } else if (currPath.includes('/team')) {
      navigate(`/team/${companyId}`);
    } else if (currPath.includes('/all-filings')) {
      navigate(`/all-filings/${companyId}`);
    } else if (currPath.includes('/role-permissions')) {
      navigate(`/role-permissions/${companyId}`);
    } else if (currPath.includes('/integration') && currPath.includes('/docs')) {
      navigate(`/integration/${companyId}/docs`);
    } else if (currPath.includes('/integration')) {
      navigate(`/integration/${companyId}`);
    }
  };

  return (
    <div ref={containerRef} className="main-container mstyle-a">
      {/* New deploy detected → prompt (not force) refresh */}
      <NewVersionBanner />
      {/* Modern Minimalist Sidebar */}
      <div ref={sidebarRef} className="main-sidebar modern-sidebar">
        <div className="main-sidebar-inner">
          {/* Brand Section - Single logo image (1.0 style with overflow clip trick) */}
          <div className="main-sidebar-brand">
            <a href="/">
              <img className="sidebar-brand-img" src="/images/logo-dark.png" alt="MBRS.ai" />
            </a>
          </div>
          
          {/* Main Navigation */}
          <div className="main-sidebar-menu">
            <ul className="menu-nav">
              <li className={`menu-item ${location.pathname.includes('/all-filings') ? 'menu-item-active' : ''}`}>
                <Link to={`/all-filings/${selectedCompany?.company_id || ''}`} className="menu-link" title="All Filings">
                  <svg className="menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14,2 14,8 20,8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  <span className="menu-title">All Filings</span>
                </Link>
              </li>
              {(permissions['usage_logs_view'] || permissions['bilings']) && (
                <li className={`menu-item ${location.pathname.includes('/usage-logs') ? 'menu-item-active' : ''}`}>
                  <Link to={`/usage-logs/${selectedCompany?.company_id || ''}`} className="menu-link" title="Usage Logs">
                    <svg className="menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <span className="menu-title">Usage & Billing</span>
                  </Link>
                </li>
              )}
              <li className={`menu-item ${location.pathname.includes('/team') ? 'menu-item-active' : ''}`}>
                <Link to={`/team/${selectedCompany?.company_id || ''}`} className="menu-link" title="Team">
                  <svg className="menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  <span className="menu-title">Team</span>
                </Link>
              </li>
              {permissions?.['settings_api_key'] && activeOrg?.countryIso !== 'SG' && (
                <li className={`menu-item ${location.pathname.includes('/integration') ? 'menu-item-active' : ''}`}>
                  <Link to={`/integration/${selectedCompany?.company_id || ''}`} className="menu-link" title="Developers">
                    <svg className="menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25M6.75 6.75L1.5 12l5.25 5.25M14.25 3l-4.5 18" />
                    </svg>
                    <span className="menu-title">Developers</span>
                  </Link>
                </li>
              )}
              {activeOrg?.countryIso !== 'SG' && (
              <li className={`menu-item ${location.pathname === '/learn-rapid-tagger' ? 'menu-item-active' : ''}`}>
                <Link to="/learn-rapid-tagger" className="menu-link" title="Learn Rapid Tagger">
                  <svg className="menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>                  
                  <span className="menu-title">Learn Rapid Tagger</span>
                </Link>
              </li>
              )}
            </ul>
          </div>

          {isAdmin && (<div className="sidebar-company-menu admin-section">
          <li className={`menu-item ${location.pathname.includes('/admin') ? 'menu-item-active' : ''}`}>
            {/* Admin Dropup Menu */}
            {showAdminDropdown && (
              <ul className="submenu-nav submenu-dropup">
                <li className="submenu-item">
                  <Link to="/admin/customers" className="submenu-link">
                  {/* <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg> */}
                    <span className="menu-title">Customers</span>
                  </Link>
                </li>
                <li className="submenu-item">
                  <Link to="/admin/salesmen" className="submenu-link">
                    <span className="menu-title">Salesmen</span>
                  </Link>
                </li>
                <li className="submenu-item">
                  <Link to="/admin/permissions" className="submenu-link">
                    <span className="menu-title">Permissions</span>
                  </Link>
                </li>
                <li className="submenu-item">
                  <Link to="/admin/referrals" className="submenu-link">
                  {/* <svg className="w-5 h-5"  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg> */}
                    <span className="menu-title">Referrals</span>
                  </Link>
                </li>
                <li className="submenu-item">
                  <Link to="/admin/sales" className="submenu-link">
                    {/* <svg className="menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg> */}
                    <span className="menu-title">Sales</span>
                  </Link>
                </li>
                <li className="submenu-item">
                  <Link to="/admin/products" className="submenu-link">
                  {/* <svg className="menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l-1 7a2 2 0 01-2 2H8a2 2 0 01-2-2L5 9z" />                  
                  </svg> */}
                  <span className="menu-title">Products</span>
                  </Link>
                </li>
                <li className="submenu-item">
                  <Link to="/admin/refund-logs" className="submenu-link">
                    <span className="menu-title">Refund Logs</span>
                  </Link>
                </li>

                {isSuperadmin ? (
                <li className="submenu-item">
                  <Link to="/admin/sales-analytics" className="submenu-link">
                    <span className="menu-title">Sales Analytics</span>
                  </Link>
                </li>
                ) : null}
                {isSuperadmin ? (
                  <li className="submenu-item">
                    <Link to="/admin/usage-analytics" className="submenu-link">
                      <span className="menu-title">Usage Analytics</span>
                    </Link>
                  </li>
                ) : null}
              </ul>
            )}
            
            <div 
              className="menu-link" 
              title="Admin"
              onClick={() => setShowAdminDropdown(!showAdminDropdown)}
              style={{ cursor: 'pointer' }}
            >
              <svg className="menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              {(
                <div className="flex items-center justify-between w-full">
                  <span className="menu-title">Admin</span>
                  <svg 
                    className={`w-4 h-4 transition-transform ${showAdminDropdown ? '' : 'rotate-180'}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              )}
            </div>
          </li>
          </div>)}
          
          {/* Company Profile and Logout - Same pattern as main menu */}
          <div className="sidebar-company-menu">
            <ul className="menu-nav">
              <li className={`menu-item ${location.pathname.includes('/settings') ? 'menu-item-active' : ''}`}>
                <Link to={`/settings/${selectedCompany?.company_id}`} className="menu-link" title="Settings">
                  <svg className="menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="menu-title">Settings</span>
                </Link>
              </li>
              <li className="menu-item">
                <button className="menu-link logout-btn" title="Logout" onClick={handleSignOut}>
                  <svg className="menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16,17 21,12 16,7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  <span className="menu-title">Logout</span>
                </button>
              </li>
            </ul>
          </div>
          
          {/* User Profile at Bottom with Company Dropdown */}
          <div className="main-sidebar-footer" style={{ padding: '16px 10px' }}>
            <div className="profile-section">
              <div className="profile">
                <div className="profile-avatar">
                <div className="avatar-placeholder">
                  {(profile?.display_name || user?.email || 'G').charAt(0).toUpperCase()}
                </div>
                </div>
                <div className="profile-info">
                    {/* Company Dropdown */}
                    <div className="relative">
                    <button
                      type="button"
                      data-tour="create-org-menu-trigger"
                      onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                      className="w-full text-left flex items-center justify-between hover:bg-gray-100 rounded px-2 py-1 -mx-2 -my-1"
                    >
                      <div className="flex-1" style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                        <span className="profile-info-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {selectedCompany?.company_name || 'Select Company'}
                        </span>
                        {(() => {
                          const iso = userCompanies.find(c => c.company_id === selectedCompany?.company_id)?.country_iso;
                          return iso ? (
                            <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', background: 'rgba(255,255,255,0.15)', borderRadius: 3, padding: '1px 4px', flexShrink: 0, letterSpacing: 0.3 }}>
                              {iso}
                            </span>
                          ) : null;
                        })()}
                      </div>
                      
                      {/* Organization Icon */}
                      <div className="flex items-center space-x-1.5 flex-shrink-0 ml-2">
                        <svg 
                          className={`w-4 h-4 text-gray-400 transition-transform ${showCompanyDropdown ? '' : 'rotate-180'}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>                      
                      {/* Dropdown Menu */}
                      {showCompanyDropdown && (
                        <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                          {userCompanies.map((company) => (
                            <button
                              key={company.company_id}
                              onClick={() => handleCompanyChange(company)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 first:rounded-t-lg ${
                                selectedCompany?.company_id === company.company_id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                              }`}
                            >
                              <div className="font-medium">
                                {company.company_name}
                                {company.country_iso && (
                                  <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: '#6b7280', background: '#f3f4f6', borderRadius: 3, padding: '1px 5px' }}>
                                    {company.country_iso}
                                  </span>
                                )}
                              </div>
                            </button>
                          ))}
                          <div className="border-t border-gray-200" />
                            <button
                              type="button"
                              data-tour="create-org-new-button"
                              onClick={() => {
                                setShowCompanyDropdown(false);
                                setShowCreateOrgModal(true);
                              }}
                              className="w-full text-left px-3 py-2.5 text-sm font-medium text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                            >
                              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                              </svg>
                              New Organization    
                            </button>
                        </div>
                      )}
                    </div>
                    <div className="profile-info-email">
                      {user?.email || 'guest@example.com'}
                    </div>
                  </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Toggle Button */}
        <div className="main-sidebar-toggle">
          <button className="btn-sidebar-toggle" onClick={toggleSidebar}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path ref={togglePathRef} d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div ref={mainRef} className="main">
        <div className="main-wrap">
          {typeof children === 'function' ? children({ toggleSidebar }) : children}
        </div>
      </div>

      <CreateOrganizationModal
        open={showCreateOrgModal}
        defaultCountry={createOrgDefaultCountry}
        onClose={() => { setShowCreateOrgModal(false); setCreateOrgDefaultCountry(undefined); }}
        onCreated={(company) => {
          handleCompanyChange(company);
        }}
      />
    </div>
  );
};
